import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BookOpen, Loader2, Scroll, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  isStoriesIncomplete,
  loadMarkandeyaPuranam,
  parseContributorStories,
  saveContributorStory,
  saveContributorStories,
} from "@/lib/puranas/markandeya-puranam-service";

function SectionCard({ title, icon: Icon, children, testId }) {
  return (
    <Card
      className="bg-gradient-to-br from-primary/5 via-white to-secondary/5 border-primary/20 shadow-card"
      data-testid={testId}
    >
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-xl flex items-center gap-2 text-primary">
          {Icon ? <Icon className="w-5 h-5" /> : null}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="text-sm text-foreground/90 space-y-3">{children}</CardContent>
    </Card>
  );
}

function StoryCard({ story }) {
  return (
    <Card className="bg-white/90 border-primary/15 hover:shadow-float transition-all">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-heading text-lg font-medium">{story.title}</h3>
          {story.source === "contributor" && (
            <Badge variant="outline" className="shrink-0 border-primary/40 text-primary">
              Contributed
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">{story.summary}</p>
        {story.teaching ? (
          <p className="text-sm mt-3 italic text-primary/90 border-l-2 border-primary/30 pl-3">
            {story.teaching}
          </p>
        ) : null}
        {story.characters?.length > 0 && (
          <p className="text-xs text-muted-foreground mt-3">
            Characters: {story.characters.join(", ")}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function IncompleteStoriesPrompt({ data, onSaved }) {
  const [bulkText, setBulkText] = useState("");
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [teaching, setTeaching] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const expected = data.expected_story_count ?? 0;
  const current = data.stories?.length ?? 0;
  const remaining = Math.max(0, expected - current);

  const handleSaveSingle = async () => {
    if (!title.trim() || !summary.trim()) return;
    setSaving(true);
    setMessage(null);
    const result = await saveContributorStory({
      title: title.trim(),
      summary: summary.trim(),
      teaching: teaching.trim(),
      characters: [],
      order: current + 1,
    });
    if (result.ok) {
      setTitle("");
      setSummary("");
      setTeaching("");
      setMessage("Story saved. Thank you — the section is updating.");
      onSaved(await loadMarkandeyaPuranam());
    } else {
      setMessage(result.error ?? "Could not save story.");
    }
    setSaving(false);
  };

  const handleSaveBulk = async () => {
    const parsed = parseContributorStories(bulkText);
    if (parsed.length === 0) return;
    setSaving(true);
    setMessage(null);
    const result = await saveContributorStories(parsed);
    if (result.ok) {
      setBulkText("");
      setMessage(`Saved ${parsed.length} story/stories. Thank you for helping complete this section.`);
      onSaved(await loadMarkandeyaPuranam());
    } else {
      setMessage(result.error ?? "Could not save stories.");
    }
    setSaving(false);
  };

  return (
    <Card
      className="mb-8 border-primary/40 bg-gradient-to-br from-primary/10 via-amber-50/80 to-white shadow-float"
      data-testid="markandeya-incomplete-prompt"
    >
      <CardHeader>
        <CardTitle className="font-heading text-lg text-primary flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          Help complete this section
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Please provide the remaining stories of Markandeya Puranam so we can complete this section.
        </p>
        <p className="text-xs text-muted-foreground">
          {current} of {expected} stories loaded · {remaining} remaining
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <p className="text-sm font-medium">Add one story</p>
          <Input
            placeholder="Story title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="markandeya-story-title"
          />
          <Textarea
            placeholder="Summary of the story"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            rows={3}
            data-testid="markandeya-story-summary"
          />
          <Textarea
            placeholder="Key teaching (optional)"
            value={teaching}
            onChange={(e) => setTeaching(e.target.value)}
            rows={2}
          />
          <Button
            onClick={handleSaveSingle}
            disabled={saving || !title.trim() || !summary.trim()}
            data-testid="markandeya-save-story"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save story"}
          </Button>
        </div>

        <div className="space-y-3 border-t border-primary/20 pt-6">
          <p className="text-sm font-medium">Or paste several stories</p>
          <p className="text-xs text-muted-foreground">
            Separate each story with a blank line. First line = title; following lines = summary.
          </p>
          <Textarea
            placeholder={"Madhu and Kaitabha\nThe Goddess awakened Viṣṇu from yoga-nidrā to slay the demons born of ear-wax...\n\nAnother story title\nSummary text here..."}
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            rows={6}
            data-testid="markandeya-bulk-stories"
          />
          <Button variant="outline" onClick={handleSaveBulk} disabled={saving || !bulkText.trim()}>
            Save pasted stories
          </Button>
        </div>

        {message && <p className="text-sm text-primary">{message}</p>}
      </CardContent>
    </Card>
  );
}

export function MarkandeyaPuranamPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const loaded = await loadMarkandeyaPuranam();
      setData(loaded);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    }
  }, []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen py-24 px-4 text-center">
        <p className="text-muted-foreground mb-4">{error ?? "Content unavailable."}</p>
        <Button asChild variant="outline">
          <Link to="/library">Back to Sacred Library</Link>
        </Button>
      </div>
    );
  }

  const incomplete = isStoriesIncomplete(data);

  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid="markandeya-puranam-page">
      <div className="max-w-3xl mx-auto">
        <Button variant="ghost" size="sm" asChild className="mb-4">
          <Link to="/library">
            <ArrowLeft className="w-4 h-4 mr-1" /> Sacred Library
          </Link>
        </Button>

        <Card className="bg-gradient-to-br from-primary/10 via-white to-secondary/5 border-primary/25 shadow-card mb-8 overflow-hidden">
          <CardHeader>
            <Badge variant="outline" className="w-fit mb-2 border-primary/50 text-primary">
              Purāṇa
            </Badge>
            <CardTitle className="font-heading text-3xl md:text-4xl">{data.title}</CardTitle>
            <p className="text-sm text-muted-foreground">
              {data.stories?.length ?? 0}
              {data.expected_story_count ? ` of ${data.expected_story_count}` : ""} stories
            </p>
          </CardHeader>
        </Card>

        {incomplete && <IncompleteStoriesPrompt data={data} onSaved={setData} />}

        <div className="space-y-8">
          <SectionCard title="Overview" icon={BookOpen} testId="markandeya-overview">
            <p className="leading-relaxed">{data.overview}</p>
          </SectionCard>

          <section data-testid="markandeya-stories">
            <h2 className="font-heading text-2xl mb-4 flex items-center gap-2 text-primary">
              <Scroll className="w-5 h-5" />
              Stories
            </h2>
            {data.stories?.length ? (
              <div className="grid gap-4">
                {data.stories.map((story) => (
                  <StoryCard key={story.id} story={story} />
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">No stories yet.</p>
            )}
          </section>

          <SectionCard title="Key Teachings" icon={Sparkles} testId="markandeya-teachings">
            <ul className="list-disc pl-5 space-y-2">
              {(data.key_teachings ?? []).map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          </SectionCard>

          <SectionCard title="Symbolic Elements" icon={Sparkles} testId="markandeya-symbols">
            <div className="grid gap-3">
              {(data.symbolic_elements ?? []).map((el) => (
                <div
                  key={el.symbol}
                  className="p-3 rounded-lg bg-white/70 border border-primary/15"
                >
                  <p className="font-medium text-primary">{el.symbol}</p>
                  <p className="text-muted-foreground mt-1">{el.meaning}</p>
                </div>
              ))}
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  );
}

export default MarkandeyaPuranamPage;
